import { useState, useEffect } from 'react';
import { motion } from 'motion/react';

interface StripeModalProps {
  onClose(): void;
}

export default function StripeModal({ onClose }: StripeModalProps) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // Check if user is already pro locally
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSubscribed(localStorage.getItem('protolive_pro_subscribed') === 'true');
  }, []);

  function handleSubscribe() {
    setIsRedirecting(true);
    
    // Simulate opening of secure Stripe Checkout payment link
    const paymentLink = import.meta.env.VITE_STRIPE_PAYMENT_LINK || 'https://checkout.stripe.com/pay/mock_byok_protolive_license_5_usd';
    
    setTimeout(() => {
      // Set local persistent Pro state
      localStorage.setItem('protolive_pro_subscribed', 'true');
      setIsSubscribed(true);
      setIsRedirecting(false);
      
      // Attempt redirect to secure payment engine
      window.open(paymentLink, '_blank', 'noopener,noreferrer');
      
      // Dispatch custom event to notify Header companion element
      window.dispatchEvent(new Event('protolive:pro_status_changed'));
    }, 1200);
  }

  function handleCancelSubscription() {
    if (confirm('Are you sure you want to pause or deactivate your Pro BYOK license?')) {
      localStorage.removeItem('protolive_pro_subscribed');
      setIsSubscribed(false);
      window.dispatchEvent(new Event('protolive:pro_status_changed'));
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="modal-backdrop"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 30, stiffness: 350 }}
        className="modal glass glass--strong"
        style={{ maxWidth: '420px', width: '100%', padding: '24px', borderRadius: 'var(--radius-lg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
            <h2 className="sc" style={{ margin: 0, fontSize: '13px', fontWeight: 'bold', letterSpacing: '0.04em' }}>Stripe Engine</h2>
          </div>
          <button className="btn btn--ghost" style={{ minHeight: '24px', padding: '0 8px' }} onClick={onClose}>
            ✕
          </button>
        </div>

        {!isSubscribed ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* Standard Unsubscribed Product Card Layout & pricing details */}
            <div style={{ background: 'var(--glass)', border: '1px solid var(--line)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                <span className="sc" style={{ fontWeight: 'bold', fontSize: '13px' }}>Pro BYOK Access</span>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>$5.00 <span style={{ fontSize: '10px', fontWeight: '400', color: 'var(--fg-dim)' }}>/ mo</span></span>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--fg-dim)', margin: 0, lineHeight: '1.4' }}>
                Unlocks full unrestricted pipeline access to all Bring-Your-Own-Key (BYOK) model connections.
              </p>
            </div>

            {/* Bullet list of model access details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 0' }}>
              <span className="sc" style={{ fontSize: '10px', color: 'var(--fg-dim)', fontWeight: 'bold' }}>BYOK Channels Unlocked:</span>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '11px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span>✓</span>
                  <span><strong>Google Gemini:</strong> Unlimited orchestration for flash & pro engines.</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span>✓</span>
                  <span><strong>Anthropic Claude & OpenAI GPT:</strong> Infinite high-fidelity cinematic prompt expansions.</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span>✓</span>
                  <span><strong>ElevenLabs API:</strong> Full synthesizers, voice clones, and automated audio assets.</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span>✓</span>
                  <span><strong>Meshy 3D Studio:</strong> Streamlined GLB models and viewport unskinned wireframe maps.</span>
                </div>
              </div>
            </div>

            <hr className="divider" style={{ margin: '4px 0' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button 
                type="button" 
                className="btn btn--primary" 
                disabled={isRedirecting}
                onClick={handleSubscribe}
                style={{ minHeight: '38px', fontSize: '11px', width: '100%', justifyContent: 'center' }}
              >
                {isRedirecting ? 'Redirecting to Stripe...' : 'Subscribe with Stripe Checkout'}
              </button>
              <span style={{ fontSize: '9px', color: 'var(--fg-dim)', textAlign: 'center', display: 'block' }}>
                Secure payments processed directly by Stripe. Active immediately.
              </span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '12px 0', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--glass-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--line-strong)', color: 'var(--fg)', fontSize: '20px' }}>
              ✓
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span className="sc" style={{ fontWeight: 'bold', fontSize: '13px' }}>Pro License Active</span>
              <p style={{ fontSize: '11px', color: 'var(--fg-dim)', margin: 0, lineHeight: '1.4', maxWidth: '320px' }}>
                Your $5 Pro plan is fully secured and active. Unlimited BYOK model connections are now enabled on this browser.
              </p>
            </div>

            <hr className="divider" style={{ width: '100%', margin: '6px 0' }} />

            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              <button className="btn btn--ghost" style={{ flex: 1, minHeight: '30px', fontSize: '10px' }} onClick={handleCancelSubscription}>
                Cancel License
              </button>
              <button className="btn btn--primary" style={{ flex: 1, minHeight: '30px', fontSize: '10px' }} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
